/**
 * Pack size tiles show the price for the purchase option the shopper has picked.
 * The canonical selling_plan select in the product form is the source of truth.
 */
export function tilePriceState(mode, dataset = {}, planId = "", formatMoney = () => "") {
  const minor = (value) => {
    const amount = Number(value);
    return value !== "" && value != null && Number.isSafeInteger(amount) ? amount : null;
  };
  const plan = String(dataset.planPrices || "").split(",").find((entry) => entry.startsWith(`${planId}:`));
  const planValue = planId && plan ? plan.split(":")[1] : null;
  const planCents = planValue != null && /^\d+$/.test(planValue) ? Number(planValue) : null;
  const packCount = Number(dataset.packCount);
  const hasPlanPrice = Number.isSafeInteger(planCents) && planCents >= 0;
  const subscribed = mode === "subscription" && (planId ? hasPlanPrice : Boolean(dataset.subscription));
  const subscriptionTotal = hasPlanPrice ? formatMoney(planCents) : dataset.subscription || "";
  const subscriptionEach = hasPlanPrice && Number.isSafeInteger(packCount) && packCount > 0
    ? formatMoney(Math.round(planCents / packCount)) : dataset.subscriptionEach || "";
  return {
    total: subscribed ? subscriptionTotal : dataset.oneTime || "",
    each: subscribed ? subscriptionEach : dataset.oneTimeEach || "",
    compare: subscribed && subscriptionTotal !== dataset.oneTime ? dataset.oneTime : "",
    priceCents: subscribed ? (hasPlanPrice ? planCents : minor(dataset.subscriptionCents)) : minor(dataset.oneTimeCents),
  };
}

/** "Ships free" holds only while the tile's active price meets the threshold. */
export function tileShipsFree(priceCents, thresholdCents) {
  const price = Number(priceCents);
  const threshold = Number(thresholdCents);
  if (priceCents == null || priceCents === "" || !Number.isSafeInteger(price)) return null;
  if (!Number.isSafeInteger(threshold) || threshold <= 0) return null;
  return price >= threshold;
}

function currentChoice(root) {
  const select = root.querySelector("[data-purchase-select]");
  return { mode: select?.value ? "subscription" : "one-time", planId: select?.value || "" };
}

export function applyTileMode(root, choice = currentChoice(root)) {
  root.querySelectorAll("[data-tsf-tile-price]").forEach((price) => {
    const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency", currency: price.dataset.currencyCode || window.Shopify?.currency?.active || "USD",
    }).format(cents / 100);
    const state = tilePriceState(choice.mode, price.dataset, choice.planId, money);
    const tile = price.closest(".tsf-quantity-tile") || price.parentElement;
    const total = price.querySelector("[data-tsf-tile-total]");
    const compare = price.querySelector("[data-tsf-tile-compare]");
    const each = tile?.querySelector("[data-tsf-tile-each]");
    if (total && state.total) total.textContent = state.total;
    if (compare) {
      compare.textContent = state.compare;
      compare.hidden = !state.compare;
    }
    if (each && state.each) each.textContent = state.each;
    const shipsFree = tile?.querySelector("[data-tsf-tile-ships-free]");
    if (shipsFree) {
      const qualifies = tileShipsFree(state.priceCents, shipsFree.dataset.thresholdCents);
      if (qualifies != null) shipsFree.hidden = !qualifies;
    }
    const savings = tile?.querySelector(".tsf-pack-savings");
    if (savings) {
      const baseline = Number(savings.dataset.baselineTotalCents);
      const saved = baseline - state.priceCents;
      savings.hidden = !Number.isSafeInteger(saved) || saved <= 0;
      if (!savings.hidden) {
        const amount = money(saved);
        savings.textContent = savings.dataset.savingsTemplate?.replace("__AMOUNT__", amount) || `Save ${amount} total`;
      }
    }
  });
}

if (typeof document !== "undefined" && !window.__tsfQuantityTiles) {
  window.__tsfQuantityTiles = true;
  const scopeOf = (node) =>
    (node instanceof Element && node.closest(".shopify-section, dialog")) || document;

  document.addEventListener("thinslim:purchase:change", (event) => {
    const mode = event.detail?.mode;
    applyTileMode(scopeOf(event.target), {
      mode: mode === "unresolved" ? "one-time" : mode,
      planId: event.detail?.planId || "",
    });
  });

  document.addEventListener("shopify:product:select", (event) => {
    const scope = scopeOf(event.target);
    event.promise
      ?.then(() => requestAnimationFrame(() => applyTileMode(scope)))
      .catch(() => {});
  });

  const init = () => applyTileMode(document);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
}
